export type MediaTrackCapabilitiesMap = { [key: string]: MediaTrackCapabilities[] };

interface FocusDistance {
  min: number;
  max: number;
  step: number;
}

async function getCameraWithClosestFocus(): Promise<string | undefined> {
  const cameras = await getCameras();
  if (!cameras || cameras.length === 0) {
    return undefined;
  }

  // Détecter le type de plateforme
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isMacOS = /Mac|Macintosh|MacIntel|MacPPC|Mac68K/i.test(navigator.userAgent);
  const isWindows = /Win|Windows/i.test(navigator.userAgent);
  const isDesktop = !isMobile;
  
  console.log('Plateforme détectée:', { isMobile, isMacOS, isWindows, isDesktop, userAgent: navigator.userAgent });
  console.log('Caméras disponibles:', cameras.map(c => ({ id: c.deviceId, label: c.label })));
  
  let candidateCameras = cameras;
  
  // Sur mobile, filtrer d'abord pour ne garder que les caméras arrière
  if (isMobile) {
    const backCameras = cameras.filter(camera => {
      const label = camera.label.toLowerCase();
      return label.includes('back') ||
             label.includes('rear') ||
             label.includes('arrière') ||
             label.includes('environment') ||
             label.includes('facing back') ||
             label.includes('world') ||
             // Patterns supplémentaires pour Android
             label.includes('camera 0') || // Souvent la caméra principale arrière
             label.includes('camera2 0') || // API Camera2
             label.includes('main') ||
             // Exclure explicitement les caméras avant
             (label.includes('camera') &&
              !label.includes('front') &&
              !label.includes('user') &&
              !label.includes('face') &&
              !label.includes('selfie') &&
              !label.includes('inner'));
    });
    
    // Si on trouve des caméras arrière, les utiliser exclusivement
    if (backCameras.length > 0) {
      candidateCameras = backCameras;
      console.log('Caméras arrière trouvées sur mobile:', backCameras.map(c => c.label));
    } else {
      // Fallback: essayer de détecter par l'ordre (généralement caméra 0 = arrière)
      const orderedCameras = cameras.sort((a, b) => {
        const aLabel = a.label.toLowerCase();
        const bLabel = b.label.toLowerCase();
        
        // Priorité aux caméras avec des indices plus bas (souvent arrière)
        const aIndex = aLabel.match(/camera\s*(\d+)/)?.[1];
        const bIndex = bLabel.match(/camera\s*(\d+)/)?.[1];
        
        if (aIndex && bIndex) {
          return parseInt(aIndex) - parseInt(bIndex);
        }
        
        // Priorité aux caméras sans "front" dans le nom
        if (aLabel.includes('front') && !bLabel.includes('front')) return 1;
        if (!aLabel.includes('front') && bLabel.includes('front')) return -1;
        
        return 0;
      });
      
      candidateCameras = orderedCameras;
      console.log('Aucune caméra arrière identifiée explicitement, utilisation de l\'ordre par défaut:', candidateCameras.map(c => c.label));
    }
  } else if (isDesktop) {
    // Sur desktop (macOS, Windows, Linux), préférer les caméras de meilleure qualité
    const highQualityCameras = cameras.filter(camera => {
      const label = camera.label.toLowerCase();
      // Préférer les caméras externes, USB, ou de marque connue
      return label.includes('usb') ||
             label.includes('external') ||
             label.includes('webcam') ||
             label.includes('logitech') ||
             label.includes('microsoft') ||
             label.includes('creative') ||
             label.includes('razer') ||
             // Sur macOS, éviter la caméra FaceTime intégrée si d'autres sont disponibles
             (isMacOS && !label.includes('facetime') && !label.includes('built-in')) ||
             // Sur Windows, préférer les caméras non-intégrées
             (isWindows && !label.includes('integrated') && !label.includes('built-in'));
    });
    
    if (highQualityCameras.length > 0) {
      candidateCameras = highQualityCameras;
      console.log('Caméras de qualité trouvées sur desktop:', highQualityCameras.map(c => c.label));
    } else {
      // Si pas de caméra externe, utiliser toutes les caméras mais trier par préférence
      candidateCameras = cameras.sort((a, b) => {
        const aLabel = a.label.toLowerCase();
        const bLabel = b.label.toLowerCase();
        
        // Préférer les caméras avec des résolutions plus élevées (souvent dans le nom)
        if (aLabel.includes('hd') || aLabel.includes('1080') || aLabel.includes('720')) return -1;
        if (bLabel.includes('hd') || bLabel.includes('1080') || bLabel.includes('720')) return 1;
        
        return 0;
      });
      console.log('Aucune caméra externe trouvée sur desktop, utilisation de toutes les caméras triées:', candidateCameras.map(c => c.label));
    }
  }

  // Maintenant appliquer l'algorithme de focus distance sur les caméras candidates
  const capabilities = await getCapabilities();
  let minFocusDistance = Number.MAX_SAFE_INTEGER;
  let bestDeviceId: string | undefined;
  let bestCamera: MediaDeviceInfo | undefined;

  // Parcourir uniquement les caméras candidates
  for (const camera of candidateCameras) {
    const deviceCapabilities = capabilities[camera.deviceId];
    if (deviceCapabilities) {
      const focusDistance = getFocusDistance(deviceCapabilities);
      console.log(`Caméra ${camera.label}: focus distance =`, focusDistance);

      if (focusDistance && focusDistance.min < minFocusDistance) {
        minFocusDistance = focusDistance.min;
        bestDeviceId = camera.deviceId;
        bestCamera = camera;
      }
    }
  }

  // Si aucune caméra avec focus distance n'est trouvée, appliquer une logique de fallback
  if (!bestDeviceId && candidateCameras.length > 0) {
    if (isDesktop) {
      // Sur desktop, préférer les caméras avec de meilleures spécifications
      const preferredCamera = candidateCameras.find(camera => {
        const label = camera.label.toLowerCase();
        return (isMacOS && !label.includes('facetime')) ||
               (isWindows && !label.includes('integrated')) ||
               label.includes('hd') ||
               label.includes('1080') ||
               label.includes('720') ||
               label.includes('usb');
      });
      bestCamera = preferredCamera || candidateCameras[0];
    } else {
      bestCamera = candidateCameras[0];
    }
    
    bestDeviceId = bestCamera.deviceId;
    console.log('Aucune focus distance trouvée, utilisation de la caméra de fallback:', bestCamera.label);
  }

  if (bestCamera) {
    console.log('Caméra sélectionnée:', bestCamera.label, 'ID:', bestDeviceId);
  }

  return bestDeviceId;
}

async function getCameras(): Promise<MediaDeviceInfo[] | undefined> {
  if (!navigator.mediaDevices.enumerateDevices) {
    return undefined;
  }

  const devices = await navigator.mediaDevices.enumerateDevices();

  return devices.filter((x) => x.kind === "videoinput");
}

function getCapability<T>(value: MediaTrackCapabilities, key: string): T | undefined {
  if (key in value) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    return (value as unknown as any)[key] as T | undefined;
  }

  return undefined;
}

function getCapabilities(value: string): Promise<MediaTrackCapabilities[] | undefined>;
function getCapabilities(value: MediaDeviceInfo): Promise<MediaTrackCapabilities[]>;
function getCapabilities(): Promise<MediaTrackCapabilitiesMap>;
async function getCapabilities(value: MediaDeviceInfo | string | undefined = undefined): Promise<MediaTrackCapabilitiesMap | MediaTrackCapabilities[] | undefined> {
  if (!value) {
    const devices = await getCameras();
    const resp: MediaTrackCapabilitiesMap = {};

    if (devices?.length) {
      for (const device of devices) {
        const capabilities = await getCapabilities(device);

        resp[device.deviceId] = capabilities;
      }
    }

    return resp;
  } else {
    // Single device
    const device = typeof value === "string" ? (await getCameras())?.find((x) => x.deviceId === value) : value;

    if (!device) {
      return undefined;
    }

    try {
      const media = await navigator.mediaDevices.getUserMedia({ video: { deviceId: device.deviceId } });
      const tracks = media.getTracks();
      const capabilities = tracks.map((track) => track.getCapabilities());

      tracks.forEach((track) => track.stop());

      return capabilities;
    } catch (error) {
      console.error('Erreur lors de l\'accès à la caméra:', error);
      return [];
    }
  }
}

function getFocusDistance(value: MediaTrackCapabilities[]): FocusDistance | undefined {
  return value
    .map((x) => {
      return getCapability<{ min: number; max: number; step: number }>(x, "focusDistance");
    })
    .find((x): x is FocusDistance => x !== undefined);
}

function getHasAutoFocus(value: MediaTrackCapabilities[]): boolean {
  return value.some((x) => {
    const focusMode = getCapability<string[]>(x, "focusMode");

    return focusMode?.includes("auto") || false;
  });
}

// Fonction pour lire l'orientation EXIF d'une image
function getImageOrientation(file: File): Promise<number> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      const dataView = new DataView(arrayBuffer);
      
      // Vérifier si c'est un JPEG
      if (dataView.getUint16(0) !== 0xFFD8) {
        resolve(1); // Pas de rotation nécessaire
        return;
      }
      
      let offset = 2;
      let marker = dataView.getUint16(offset);
      
      // Chercher le marqueur EXIF
      while (offset < dataView.byteLength) {
        if (marker === 0xFFE1) {
          // Marqueur EXIF trouvé
          const exifLength = dataView.getUint16(offset + 2);
          const exifData = new DataView(arrayBuffer, offset + 4, exifLength - 2);
          
          // Vérifier la signature EXIF
          if (exifData.getUint32(0) === 0x45786966) { // "Exif"
            const orientation = getOrientationFromExif(exifData);
            resolve(orientation);
            return;
          }
        }
        
        offset += 2 + dataView.getUint16(offset + 2);
        if (offset >= dataView.byteLength) break;
        marker = dataView.getUint16(offset);
      }
      
      resolve(1); // Orientation par défaut
    };
    
    reader.readAsArrayBuffer(file);
  });
}

// Fonction pour extraire l'orientation des données EXIF
function getOrientationFromExif(exifData: DataView): number {
  try {
    // Passer l'en-tête EXIF
    let offset = 6;
    
    // Lire l'ordre des octets (endianness)
    const byteOrder = exifData.getUint16(offset);
    const littleEndian = byteOrder === 0x4949;
    
    offset += 2;
    
    // Vérifier la signature TIFF
    const tiffSignature = littleEndian ? exifData.getUint16(offset, true) : exifData.getUint16(offset);
    if (tiffSignature !== 0x002A) return 1;
    
    offset += 2;
    
    // Lire l'offset vers le premier IFD
    const ifdOffset = littleEndian ? exifData.getUint32(offset, true) : exifData.getUint32(offset);
    
    // Aller au premier IFD
    offset = 6 + ifdOffset;
    
    // Lire le nombre d'entrées dans l'IFD
    const numEntries = littleEndian ? exifData.getUint16(offset, true) : exifData.getUint16(offset);
    offset += 2;
    
    // Chercher le tag d'orientation (0x0112)
    for (let i = 0; i < numEntries; i++) {
      const tag = littleEndian ? exifData.getUint16(offset, true) : exifData.getUint16(offset);
      
      if (tag === 0x0112) { // Tag d'orientation
        const type = littleEndian ? exifData.getUint16(offset + 2, true) : exifData.getUint16(offset + 2);
        const count = littleEndian ? exifData.getUint32(offset + 4, true) : exifData.getUint32(offset + 4);
        
        if (type === 3 && count === 1) { // Type SHORT, count 1
          const orientation = littleEndian ? exifData.getUint16(offset + 8, true) : exifData.getUint16(offset + 8);
          return orientation;
        }
      }
      
      offset += 12; // Chaque entrée IFD fait 12 octets
    }
  } catch (error) {
    console.error('Erreur lors de la lecture des données EXIF:', error);
  }
  
  return 1; // Orientation par défaut
}

// Fonction pour corriger l'orientation d'une image
function correctImageOrientation(imageDataUrl: string, orientation: number): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        resolve(imageDataUrl);
        return;
      }
      
      let { width, height } = img;
      
      // Définir les dimensions du canvas selon l'orientation
      switch (orientation) {
        case 5:
        case 6:
        case 7:
        case 8:
          // Rotation de 90° ou 270° - inverser largeur et hauteur
          canvas.width = height;
          canvas.height = width;
          break;
        default:
          canvas.width = width;
          canvas.height = height;
      }
      
      // Appliquer les transformations selon l'orientation EXIF
      switch (orientation) {
        case 2:
          // Miroir horizontal
          ctx.transform(-1, 0, 0, 1, width, 0);
          break;
        case 3:
          // Rotation 180°
          ctx.transform(-1, 0, 0, -1, width, height);
          break;
        case 4:
          // Miroir vertical
          ctx.transform(1, 0, 0, -1, 0, height);
          break;
        case 5:
          // Miroir horizontal + rotation 90° sens horaire
          ctx.transform(0, 1, 1, 0, 0, 0);
          break;
        case 6:
          // Rotation 90° sens horaire
          ctx.transform(0, 1, -1, 0, height, 0);
          break;
        case 7:
          // Miroir horizontal + rotation 90° sens anti-horaire
          ctx.transform(0, -1, -1, 0, height, width);
          break;
        case 8:
          // Rotation 90° sens anti-horaire
          ctx.transform(0, -1, 1, 0, 0, width);
          break;
        default:
          // Pas de transformation (orientation 1)
          break;
      }
      
      // Dessiner l'image avec les transformations appliquées
      ctx.drawImage(img, 0, 0);
      
      // Convertir en data URL
      const correctedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
      resolve(correctedDataUrl);
    };
    
    img.src = imageDataUrl;
  });
}


export {
  getCameraWithClosestFocus,
  getCameras,
  getCapabilities,
  getFocusDistance,
  getHasAutoFocus,
  getImageOrientation,
  correctImageOrientation,
};